import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { FilterInputType, FilterType, IFilterSettings } from "@features/filter";
import { CreateMode, GridComponent, IGridColumn, IGridSettings, ITableSettings } from "@features/grid";
import { FormEventsDTO, FormEventUserTypeDTO } from "@main/formio/dto/formEventsDTO";
import { FormEventsService } from "@main/formio/services/formEvents.service";
import { FormButtonsConfig } from "@mipro/components";
import { SelectItem } from "primeng/api";
import { createOptions as createEventOptions, FormEventType, FormEventTypeMap } from "./event-type";
import { FormTagDTO } from "@main/formio/dto/formTagDTO";
import { FormioTagsService } from "@main/formio/services/formio-tags.service";
import { FormIODefinition } from "@features/bb-formio";
import { IRole } from "@main/roles";
import { GoverningBodyService } from "@project/governingbody/governingbody.service";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";

import { OverlayPanel } from "primeng/overlaypanel";

@Component({
    selector: "form-events",
    templateUrl: "./form-events.component.html",
    styleUrls: ["./form-events.component.scss"]
})
export class FormEventsComponent implements OnInit {
    formEventForm: UntypedFormGroup;

    @Input() governingBodyId: string
    @Input() organizationId: string
    @Input() isGovBodyPage: boolean = false;

    isCreate = false;

    @ViewChild("userTypeOverlayPanel") userTypeOverlayPanel: OverlayPanel;

    @ViewChild(GridComponent, { static: true })
    grid: GridComponent;

    eventOptions: SelectItem[] = [];

    allDefinitions: FormIODefinition[] = [];
    definitionDropdownOptions: SelectItem[] = [];

    appRoles: IRole[];
    userTypeOptions: SelectItem[] = [];
    userTypeOptionsFilter: SelectItem[] = [];
    mandatory = false;

    allTags: FormTagDTO[];
    tagOptions: SelectItem[];
    saving = false;
    selectedFormEvent: FormEventsDTO = null;

    overlayPanelTitle: string = null;
    overlayPanelItems: string[] = [];

    formButtonsConfig = <FormButtonsConfig>{
        saveLabel: "Save",
        saveFunc: () => {
            this.save();
        },
        cancelFunc: () => {
            this.cancel();
        }
    };

    showCreateEditDialog = false;
    filterSettings: IFilterSettings[];
    public tableSettings: ITableSettings;
    public gridSettings: IGridSettings = {
        updateFunc: rowData => this.editFormEvent(rowData),
        createMode: CreateMode.Disabled
    };

    constructor(
        private formEventsService: FormEventsService,
        private formTagService: FormioTagsService,
        private governingBodyService: GoverningBodyService,
        private fb: UntypedFormBuilder
    ) {
        this.gridSettings.dataService = this.formEventsService;
    }


    private async editFormEvent(formEvent: FormEventsDTO) {
        this.isCreate = false;
        this.generateDefinitionOptions({formEventType: formEvent.eventType});

        var userTypes = formEvent.formEventUserType.map(item => item.roleId);

        this.selectedFormEvent = formEvent;

        await this.updateUserTypeOptionsAvailability(formEvent.eventType);

        this.showCreateEditDialog = true;

        this.formEventForm.patchValue({
            id: formEvent.id,
            eventType: formEvent.eventType.toString(),
            userTypes: userTypes,
            tags: [],
            formDefinition: formEvent.formDefinitionId,
            isMandatoryForm: formEvent.isMandatoryForm
        });
    }

    private resetForm(): void {
        this.formEventForm.reset();
        this.formEventForm.patchValue({
            id: null,
            eventType: null,
            userTypes: [],
            tags: [],
            formDefinition: null,
            isMandatoryForm: false
        });

    }

    private initForm(): void {
        this.formEventForm = this.fb.group({
            id: null,
            eventType: [null, Validators.required],
            userTypes: [[], Validators.required],
            tags: [[]],
            formDefinition: [null, Validators.required],
            isMandatoryForm: [false]
        });
    }

    async generateTagOptions() {
        this.allTags = await this.formTagService.getAll();

        this.tagOptions = this.allTags.map(
            tag =>
                <SelectItem>{
                    value: tag.id_original,
                    label: tag.name
                }
        );
    }

    generateDefinitionOptions(params?: {tagIds?: number[], formEventType?: FormEventType }) {
        let filteredDefinitions = this.allDefinitions;

        if (params?.tagIds && params?.tagIds.length > 0) {
            filteredDefinitions = filteredDefinitions.filter(formDef =>
                formDef.formTags?.some(tag => params?.tagIds.includes(tag.formTagId))
            );
        }

        if (params?.formEventType && params?.formEventType === FormEventType.SelfRegisterPatient) {
            filteredDefinitions = filteredDefinitions.filter(formDef => !!formDef.episodeId);
        }

        const selectedFormIsMissingFromFilteredList = !filteredDefinitions.find(filteredDef => filteredDef.id == this.selectedFormDefinitionId);
        if (this.selectedFormDefinitionId && selectedFormIsMissingFromFilteredList) {
            this.formEventForm.patchValue({formDefinition: null});
        }

        this.definitionDropdownOptions = filteredDefinitions.map(
            x =>
                <SelectItem>{
                    value: x.id,
                    label: x.name
                }
        );
    }

    async save() {
        if (this.formEventForm.invalid) {
            this.formEventForm.markAllAsTouched();
            return;
        }

        this.saving = true;
        let formEventData = this.formEventForm.value;
        let formEvent = <FormEventsDTO>{
            id: this.isCreate ? "0" : formEventData.id,
            eventType: Number(formEventData.eventType),
            governingBodyId: this.governingBodyId,
            organizationId: this.organizationId,
            isMandatoryForm: formEventData.isMandatoryForm,
            formDefinitionId: formEventData.formDefinition
        };

        formEvent.formEventUserType = formEventData.userTypes.map(
            role =>
                <FormEventUserTypeDTO>{
                    id: "0",
                    formEventId: "0",
                    roleId: role
                }
        );
        try {
            if (this.isCreate) {
                await this.formEventsService.create(formEvent);
            } else {
                await this.formEventsService.update(formEvent.id, formEvent);
            }

            this.resetForm();
            this.showCreateEditDialog = false;
            this.selectedFormEvent = null;
            this.grid.reload();
        } finally {
            this.saving = false;
        }
    }

    async onTagsChanged(event: any) {
        this.generateDefinitionOptions({tagIds: event?.value});
    }

    async addEvent() {
        this.isCreate = true;
        this.generateDefinitionOptions();
        this.showCreateEditDialog = true;
        this.disableUserTypesControl();
    }

    cancel() {
        this.resetForm();
        this.enableUserTypesControl();
        this.showCreateEditDialog = false;
        this.selectedFormEvent = null;
    }

    async eventChange(selectedEventType: FormEventType) {
        this.resetUserTypes();
        if (selectedEventType) {
            this.generateDefinitionOptions({formEventType: Number(selectedEventType)});
            this.enableUserTypesControl();
            this.updateUserTypeOptionsAvailability(selectedEventType);
        } else {
            this.generateDefinitionOptions();
            this.disableUserTypesControl();
        }
    }

    get selectedEventType() {
        return this.formEventForm.get("eventType").value;
    }

    get selectedFormDefinitionId(): string {
        return this.formEventForm.get("formDefinition").value;
    }

    disableUserTypesControl() {
        this.formEventForm.get("userTypes").disable();
    }

    resetUserTypes() {
        this.formEventForm.get("userTypes").reset([]);
    }

    enableUserTypesControl() {
        this.formEventForm.get("userTypes").enable();
    }

    async updateUserTypeOptionsAvailability (eventType: FormEventType ) {
        const assignedUserTypes = await this.formEventsService.getAssignedUserTypesForEvent(this.governingBodyId,this.organizationId , eventType, this.selectedFormEvent ? this.selectedFormEvent.id :"0");
        this.userTypeOptions.forEach(x => {
            x.disabled = assignedUserTypes.includes(x.value)
        });
    }

     async generateUserTypeOptions() {

        this.appRoles = await this.governingBodyService.getRoles(this.governingBodyId);

        this.userTypeOptions = this.appRoles.map(x => ({
             label: x.name,
             value: x.id,
             disabled: false
         }));

         this.userTypeOptionsFilter = this.userTypeOptions.map(option => ({ ...option }));
     }

    getFirstUserType(userType: FormEventUserTypeDTO[]) {
        return userType[0]?.roleName;
    }

    showTagsOverlayPanel(event: any, userType: FormEventUserTypeDTO[]) {
        this.overlayPanelItems = userType.map(x => x.roleName);
        this.overlayPanelTitle = "User Types";
        this.userTypeOverlayPanel.show(event);
    }

    hideTagsOverlayPanel(event: any) {
        this.userTypeOverlayPanel.hide();
        this.overlayPanelTitle = "";
    }

    async ngOnInit() {
        (this.gridSettings.dataService as FormEventsService).governingBodyId = this.governingBodyId;
        (this.gridSettings.dataService as FormEventsService).organizationId = this.organizationId;
        (this.gridSettings.dataService as FormEventsService).isGovgBodyPage = this.isGovBodyPage;


        this.allDefinitions = await this.formEventsService.getDefinitionsForEvents(this.governingBodyId, this.organizationId);
        this.initForm();
        this.eventOptions = createEventOptions();
        this.generateDefinitionOptions();
        await this.generateTagOptions();
        await this.generateUserTypeOptions();


        this.filterSettings = <IFilterSettings[]>[
            {
                header: "Event",
                valueFieldName: "EventType",
                inputType: FilterInputType.Multiselect,
                filterType: FilterType.Numeric,
                dropdownOptions: this.eventOptions,
                autoSubmitSelectableInputChange: true
            },
            {
                header: "User Types",
                valueFieldName: "UserTypes",
                inputType: FilterInputType.Multiselect,
                filterType: FilterType.Text,
                dropdownOptions: this.userTypeOptionsFilter,
                autoSubmitSelectableInputChange: true
            }
        ];

        this.tableSettings = {
            columns: <IGridColumn[]>[
                {
                    header: "Event",
                    field: "eventType",
                    displayHandler: (event: FormEventType) => FormEventTypeMap[event]
                },
                {
                    field: "formEventUserType",
                    header: "User Type"
                },
                {
                    field: "formDefinition.name",
                    header: "Form Name"
                }
            ]
        };
    }
}